import React, { useEffect, useRef, useState, useCallback } from "react";
import { db } from "../firebase";
import { doc, onSnapshot, setDoc } from "firebase/firestore";

const SESSION_ID = "grey";
const W = 480;
const H = 520;
const GAME_DURATION = 30;

const startSpawnInterval = 550;
const endSpawnInterval = 130;
const startLifetime = 1500;
const endLifetime = 650;
const startMaxOnScreen = 3;
const endMaxOnScreen = 8;
const startSpeed = 70;
const endSpeed = 200;

const HEART_SIZE = 56;

export default function MiniGame({ goTo }) {
  const [running, setRunning] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [scores, setScores] = useState({ you: 32, her: 0 });
  const [hearts, setHearts] = useState([]);

  const startTimeRef = useRef(0);
  const lastSpawnRef = useRef(0);
  const lastTickRef = useRef(0);
  const rafRef = useRef(null);
  const heartIdRef = useRef(0);

  useEffect(() => {
    const ref = doc(db, "sessions", SESSION_ID);
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() || {};
      setScores({
        you: data.heartHunt?.you ?? 32,
        her: data.heartHunt?.her ?? 0,
      });
    });
    return () => unsub();
  }, []);

  const lerp = (a, b, t) => a + (b - a) * t;

  const reset = useCallback(() => {
    setScore(0);
    setTimeLeft(GAME_DURATION);
    setHearts([]);
    setGameOver(false);
    setRunning(false);
    startTimeRef.current = 0;
    lastSpawnRef.current = 0;
    lastTickRef.current = 0;
    heartIdRef.current = 0;
  }, []);

  const start = () => {
    if (running) return;
    if (gameOver) reset();
    setRunning(true);
    const t0 = performance.now();
    startTimeRef.current = t0;
    lastSpawnRef.current = t0;
    lastTickRef.current = t0;
  };

  const saveHerScore = async (newScore) => {
    if (newScore <= scores.her) return;
    const ref = doc(db, "sessions", SESSION_ID);
    await setDoc(
      ref,
      { heartHunt: { her: newScore, you: scores.you } },
      { merge: true }
    );
  };

  useEffect(() => {
    if (!running) return;
    const tick = (now) => {
      const elapsed = (now - startTimeRef.current) / 1000;
      const progress = Math.min(elapsed / GAME_DURATION, 1);
      const remaining = Math.max(0, GAME_DURATION - elapsed);
      setTimeLeft(remaining);

      const dt = Math.min(0.05, (now - lastTickRef.current) / 1000);
      lastTickRef.current = now;

      if (elapsed >= GAME_DURATION) {
        setRunning(false);
        setGameOver(true);
        setScore((s) => {
          saveHerScore(s);
          return s;
        });
        return;
      }

      const spawnInterval = lerp(
        startSpawnInterval,
        endSpawnInterval,
        progress
      );
      const lifetime = lerp(startLifetime, endLifetime, progress);
      const maxOnScreen = Math.floor(
        lerp(startMaxOnScreen, endMaxOnScreen, progress)
      );
      const speed = lerp(startSpeed, endSpeed, progress);

      const dueToSpawn = now - lastSpawnRef.current > spawnInterval;
      let newHeart = null;
      if (dueToSpawn) {
        heartIdRef.current += 1;
        const angle = Math.random() * Math.PI * 2;
        newHeart = {
          id: heartIdRef.current,
          x: Math.random() * (W - HEART_SIZE - 20) + 10,
          y: Math.random() * (H - HEART_SIZE - 20) + 10,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          bornAt: now,
          lifetime,
          hit: false,
        };
      }

      setHearts((prev) => {
        const moved = prev
          .filter((h) => !h.hit && now - h.bornAt < h.lifetime)
          .map((h) => {
            let nx = h.x + h.vx * dt;
            let ny = h.y + h.vy * dt;
            let nvx = h.vx;
            let nvy = h.vy;
            if (nx < 0) {
              nx = 0;
              nvx = Math.abs(nvx);
            } else if (nx > W - HEART_SIZE) {
              nx = W - HEART_SIZE;
              nvx = -Math.abs(nvx);
            }
            if (ny < 0) {
              ny = 0;
              nvy = Math.abs(nvy);
            } else if (ny > H - HEART_SIZE) {
              ny = H - HEART_SIZE;
              nvy = -Math.abs(nvy);
            }
            return { ...h, x: nx, y: ny, vx: nvx, vy: nvy };
          });
        if (newHeart && moved.length < maxOnScreen) {
          lastSpawnRef.current = now;
          return [...moved, newHeart];
        }
        return moved;
      });

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [running]);

  const handleHeartClick = (id, e) => {
    e.stopPropagation();
    setHearts((prev) =>
      prev.map((h) => (h.id === id && !h.hit ? { ...h, hit: true } : h))
    );
    setScore((s) => s + 1);
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const t = ctx.currentTime;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(880, t);
      o.frequency.exponentialRampToValueAtTime(1320, t + 0.08);
      g.gain.setValueAtTime(0.06, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
      o.connect(g);
      g.connect(ctx.destination);
      o.start(t);
      o.stop(t + 0.14);
    } catch {}
  };

  const sheBeatYou = scores.her > scores.you;

  // Inline styles so the game works even if styles.css gets borked
  const stageStyle = {
    width: W,
    height: H,
    position: "relative",
    overflow: "hidden",
    cursor: "crosshair",
  };

  const heartBaseStyle = {
    position: "absolute",
    width: HEART_SIZE,
    height: HEART_SIZE,
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    fontSize: 38,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    filter: "drop-shadow(0 0 12px rgba(255, 77, 109, 0.7))",
    transition: "opacity 0.12s ease",
  };

  return (
    <div className="scene minigame-scene">
      <h1 className="title">heart hunt</h1>
      <p className="subtitle">click hearts before they fade · 30s</p>

      <div className="leaderboard">
        <div className="lb-leader">
          <div className="lb-name">angel</div>
          <div className="lb-score">{scores.you}</div>
        </div>
        <div className="lb-vs">vs</div>
        <div className={`lb-her ${sheBeatYou ? "winning" : ""}`}>
          <div className="lb-name">grey</div>
          <div className="lb-score">{scores.her}</div>
        </div>
      </div>

      <div className="hh-hud">
        <div className="hh-time">
          <span className="hh-label">TIME</span>
          <span className="hh-value">{Math.ceil(timeLeft)}</span>
        </div>
        <div className="hh-time">
          <span className="hh-label">SCORE</span>
          <span className="hh-value">{score}</span>
        </div>
      </div>

      <div className="flappy-stage hh-stage" style={stageStyle}>
        {hearts.map((h) => {
          const age = performance.now() - h.bornAt;
          const lifeT = age / h.lifetime;
          const opacity = h.hit ? 0 : 1 - Math.max(0, lifeT - 0.6) * 2.5;
          const scale = h.hit ? 1.6 : 1 + Math.sin(lifeT * Math.PI) * 0.06;
          return (
            <button
              key={h.id}
              data-no-sfx
              style={{
                ...heartBaseStyle,
                left: h.x,
                top: h.y,
                opacity,
                transform: `scale(${scale})`,
              }}
              onClick={(e) => handleHeartClick(h.id, e)}
            >
              🖤
            </button>
          );
        })}

        {!running && !gameOver && (
          <div className="flappy-overlay">
            <button className="btn" onClick={start}>
              <span className="btn-text">start</span>
            </button>
          </div>
        )}
        {gameOver && (
          <div className="flappy-overlay">
            <div className="flappy-msg">
              time! · {score}
              <div className="flappy-msg-sub">click start to retry</div>
              <div style={{ marginTop: 14 }}>
                <button className="btn" onClick={start}>
                  <span className="btn-text">retry</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="row" style={{ marginTop: 18 }}>
        <button className="btn ghost" onClick={() => goTo("dateAsk")}>
          <span className="btn-text">next →</span>
        </button>
      </div>
    </div>
  );
}
